<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuery } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import CatalogueDeleteConfirm from '@/components/catalogue/CatalogueDeleteConfirm.vue';
import { TranslationService } from '@/services/translation.services';

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();

/* ── Organisations (pour filtre multi-org) ─────────────────────────────── */
const { data: orgsData } = useQuery({
  queryKey: ['all-col-orgs'],
  queryFn: async () => {
    const r = await axiosInstance.get(
      'organizations/search?pagination=true&pageSize=200&sortBy=name&sortOrder=ASC',
    );
    return r.data;
  },
  staleTime: 5 * 60_000,
});
const organizations = computed<any[]>(() => {
  const d = orgsData.value as any;
  return d?.data ?? (Array.isArray(d) ? d : []);
});

const orgFilter = ref<string[]>([]);
const orgDropdownOpen = ref(false);
const orgSearch = ref('');
const filteredOrgs = computed(() =>
  organizations.value.filter((o) =>
    (o.name ?? o.designation ?? '')
      .toLowerCase()
      .includes(orgSearch.value.toLowerCase()),
  ),
);
function toggleOrg(id: string) {
  const i = orgFilter.value.indexOf(id);
  if (i >= 0) orgFilter.value.splice(i, 1);
  else orgFilter.value.push(id);
  storeFilter.value = [];
}
function clearOrgFilter() {
  orgFilter.value = [];
  orgSearch.value = '';
  storeFilter.value = [];
}
const orgFilterLabel = computed(() => {
  if (!orgFilter.value.length) return t('allCollections.allOrgs');
  if (orgFilter.value.length === 1) {
    const o = organizations.value.find((o) => o.id === orgFilter.value[0]);
    return o ? (o.name ?? o.designation) : orgFilter.value[0];
  }
  return t('allCollections.orgsSelected', { n: orgFilter.value.length });
});

/* ── Commerces de l'organisation sélectionnée (filtre store, actif si 1 seule org) ── */
const singleOrgId = computed(() =>
  orgFilter.value.length === 1 ? orgFilter.value[0] : null,
);
const { data: orgStoresData } = useQuery({
  queryKey: computed(() => ['all-col-org-stores', singleOrgId.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `restaurants/search?pagination=true&page=1&pageSize=200&organizationId=${singleOrgId.value}`,
    );
    return r.data;
  },
  enabled: computed(() => !!singleOrgId.value),
});
const orgStores = computed<any[]>(() => {
  const d = orgStoresData.value as any;
  return d?.data ?? (Array.isArray(d) ? d : []);
});

const storeFilter = ref<string[]>([]);
const storeDropdownOpen = ref(false);
const storeSearch = ref('');
const filteredStores = computed(() =>
  orgStores.value.filter((s) =>
    (s.designation ?? '')
      .toLowerCase()
      .includes(storeSearch.value.toLowerCase()),
  ),
);
function toggleStore(id: string) {
  const i = storeFilter.value.indexOf(id);
  if (i >= 0) storeFilter.value.splice(i, 1);
  else storeFilter.value.push(id);
}
function clearStoreFilter() {
  storeFilter.value = [];
  storeSearch.value = '';
}
const storeFilterLabel = computed(() => {
  if (!storeFilter.value.length) return t('allCollections.allStores');
  if (storeFilter.value.length === 1) {
    const s = orgStores.value.find((s) => s.id === storeFilter.value[0]);
    return s ? s.designation : storeFilter.value[0];
  }
  return t('allCollections.storesSelected', { n: storeFilter.value.length });
});

/* ── Tags (agrégés depuis les résultats déjà chargés — pas d'endpoint dédié) ── */
const tagFilter = ref<string[]>([]);
const tagDropdownOpen = ref(false);
const tagSearch = ref('');
const knownTags = ref<Set<string>>(new Set());
function registerTags(list: any[]) {
  list.forEach((cat) =>
    (cat.tags ?? []).forEach((tg: string) => knownTags.value.add(tg)),
  );
}
const filteredTags = computed(() =>
  [...knownTags.value].filter((tg) =>
    tg.toLowerCase().includes(tagSearch.value.toLowerCase()),
  ),
);
function toggleTag(tg: string) {
  const i = tagFilter.value.indexOf(tg);
  if (i >= 0) tagFilter.value.splice(i, 1);
  else tagFilter.value.push(tg);
}
function clearTagFilter() {
  tagFilter.value = [];
  tagSearch.value = '';
}
const tagFilterLabel = computed(() => {
  if (!tagFilter.value.length) return t('allCollections.allTags');
  if (tagFilter.value.length === 1) return tagFilter.value[0];
  return t('allCollections.tagsSelected', { n: tagFilter.value.length });
});

/* ── Vue carte / tableau ──────────────────────────────────────────────────── */
const viewMode = ref<'card' | 'table'>('card');

/* ── Avatar (initiales + couleur, même palette que la page Clients) ────────── */
const AVATAR_COLORS = [
  ['#fff7ee', '#f68402'],
  ['#eef3ff', '#3b5bdb'],
  ['#f0fff4', '#2f9e44'],
  ['#fff0f6', '#c2255c'],
  ['#f8f0fe', '#7048e8'],
  ['#e6fcf5', '#0c8599'],
  ['#fff9db', '#e67700'],
  ['#fff5f5', '#c92a2a'],
];
function avatarStyle(seed: string) {
  const i = seed.charCodeAt(0) % AVATAR_COLORS.length;
  return { background: AVATAR_COLORS[i][0], color: AVATAR_COLORS[i][1] };
}
function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2)
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

/* ── Recherche + tri + pagination ────────────────────────────────────────── */
const search = ref('');
const searchDebounced = ref('');
let debTimer: ReturnType<typeof setTimeout>;
function onSearch(v: string) {
  clearTimeout(debTimer);
  debTimer = setTimeout(() => {
    searchDebounced.value = v.trim();
    page.value = 1;
  }, 280);
}

const sortBy = ref<'createdAt' | 'name' | 'position'>('position');
const sortOrder = ref<'ASC' | 'DESC'>('ASC');
function toggleSort(key: typeof sortBy.value) {
  if (sortBy.value === key)
    sortOrder.value = sortOrder.value === 'ASC' ? 'DESC' : 'ASC';
  else {
    sortBy.value = key;
    sortOrder.value = 'ASC';
  }
  page.value = 1;
}

const page = ref(1);
const pageSize = ref(20);
const PAGE_SIZES = [10, 20, 50, 100] as const;

watch(
  [orgFilter, storeFilter, tagFilter],
  () => {
    page.value = 1;
  },
  { deep: true },
);
watch(pageSize, () => {
  page.value = 1;
});

const {
  data: apiResp,
  isPending,
  refetch: refetchCollections,
} = useQuery({
  queryKey: computed(() => [
    'all-collections',
    page.value,
    pageSize.value,
    searchDebounced.value,
    orgFilter.value,
    storeFilter.value,
    tagFilter.value,
    sortBy.value,
    sortOrder.value,
  ]),
  queryFn: async () => {
    const params = new URLSearchParams();
    params.append('pagination', 'true');
    params.append('page', String(page.value));
    params.append('pageSize', String(pageSize.value));
    params.append('sortBy', sortBy.value);
    params.append('sortOrder', sortOrder.value);
    if (searchDebounced.value) params.append('q', searchDebounced.value);
    orgFilter.value.forEach((id) => params.append('organizationIds', id));
    storeFilter.value.forEach((id) => params.append('storeIds', id));
    tagFilter.value.forEach((tg) => params.append('tags', tg));
    const r = await axiosInstance.get(
      `products/categories/search?${params.toString()}`,
    );
    return r.data;
  },
});

const rows = computed<any[]>((): any[] => (apiResp.value as any)?.data ?? []);
watch(rows, (list) => registerTags(list), { immediate: true });
const total = computed(() => (apiResp.value as any)?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / pageSize.value)),
);
const visiblePages = computed(() => {
  const t = totalPages.value;
  const cur = page.value;
  if (t <= 5) return Array.from({ length: t }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, t - 4));
  return Array.from({ length: Math.min(5, t) }, (_, i) => start + i);
});

function setPage(p: number) {
  page.value = Math.max(1, Math.min(p, totalPages.value));
}

function designationOf(cat: any): string {
  const alt = cat.designationAlternatives;
  return alt?.fr ?? cat.designation ?? '—';
}

function goToOrg(orgId: string) {
  router.push(`/clients/${orgId}`);
}
function goToStores(orgId: string) {
  router.push(`/commerces?organizationId=${orgId}`);
}

/* ── Traduction (backend interne) ────────────────────────────────────────── */
// L'ancien libreTranslate faisait un appel par langue cible ; le nouvel
// endpoint /translation renvoie toutes les langues en un seul appel, donc on
// mémoïse l'appel groupé (par texte+source) pour que les Promise.all des
// appelants (un par langue cible) ne déclenchent qu'une seule requête réseau.
let lastTranslateKey = '';
let lastTranslatePromise: Promise<{
  fr: string;
  en: string;
  nl: string;
}> | null = null;
async function libreTranslate(
  text: string,
  src: string,
  tgt: 'fr' | 'en' | 'nl',
): Promise<string> {
  const key = `${src}:${text}`;
  if (key !== lastTranslateKey || !lastTranslatePromise) {
    lastTranslateKey = key;
    lastTranslatePromise = TranslationService.translateText(
      text,
      src as 'fr' | 'en' | 'nl',
    );
  }
  const translated = await lastTranslatePromise;
  return translated[tgt];
}

/* ── Modules (canaux de vente) où une collection s'affiche ──────────────── */
type TCategoryModule =
  'click_and_collect' | 'kiosk' | 'pds_display' | 'pos_register';
const MODULE_OPTIONS: {
  id: TCategoryModule;
  icon: string;
  labelKey: string;
}[] = [
  {
    id: 'click_and_collect',
    icon: 'pi-shopping-cart',
    labelKey: 'commerceDetail.modulesCnc',
  },
  {
    id: 'pos_register',
    icon: 'pi-calculator',
    labelKey: 'commerceDetail.modulesCaisse',
  },
  {
    id: 'pds_display',
    icon: 'pi-desktop',
    labelKey: 'commerceDetail.modulesPds',
  },
  {
    id: 'kiosk',
    icon: 'pi-tablet',
    labelKey: 'commerceDetail.modulesKiosk',
  },
];
function toggleModule(list: TCategoryModule[], id: TCategoryModule) {
  const idx = list.indexOf(id);
  if (idx >= 0) list.splice(idx, 1);
  else list.push(id);
}

/* ── Création d'une collection ───────────────────────────────────────────── */
const showNewForm = ref(false);
const newOrgId = ref('');
const newParentId = ref('');
const newModules = ref<TCategoryModule[]>([]);

/* ── Organisations pour les selects de formulaire (create) ──────────────── */
const { data: newOrgCategoriesResp } = useQuery({
  queryKey: computed(() => ['all-col-org-parents', newOrgId.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(
      `products/categories/search?pagination=false&organizationIds=${newOrgId.value}`,
    );
    return r.data as any;
  },
  enabled: computed(() => !!newOrgId.value),
});
const newOrgCategories = computed<any[]>(
  () => newOrgCategoriesResp.value?.data ?? [],
);

const newLang = ref<'fr' | 'en' | 'nl'>('fr');
const newFr = ref('');
const newEn = ref('');
const newNl = ref('');
const newTranslating = ref(false);
const newDescLang = ref<'fr' | 'en' | 'nl'>('fr');
const newDescFr = ref('');
const newDescEn = ref('');
const newDescNl = ref('');
const newDescTranslating = ref(false);
const newSaving = ref(false);
const newError = ref<string | null>(null);

function openNewForm() {
  showNewForm.value = true;
  newOrgId.value = orgFilter.value.length === 1 ? orgFilter.value[0] : '';
  newParentId.value = '';
  newModules.value = [];
  newLang.value = 'fr';
  newFr.value = '';
  newEn.value = '';
  newNl.value = '';
  newDescLang.value = 'fr';
  newDescFr.value = '';
  newDescEn.value = '';
  newDescNl.value = '';
  newError.value = null;
}
function closeNewForm() {
  showNewForm.value = false;
}
async function onNewBlur(lang: 'fr' | 'en' | 'nl') {
  if (newLang.value !== lang) return;
  const text = (
    lang === 'fr' ? newFr.value : lang === 'en' ? newEn.value : newNl.value
  ).trim();
  if (!text || newTranslating.value) return;
  newTranslating.value = true;
  try {
    const targets = (['fr', 'en', 'nl'] as const).filter((l) => l !== lang);
    const results = await Promise.all(
      targets.map((t) => libreTranslate(text, lang, t)),
    );
    targets.forEach((l, i) => {
      if (l === 'fr') newFr.value = results[i];
      if (l === 'en') newEn.value = results[i];
      if (l === 'nl') newNl.value = results[i];
    });
  } finally {
    newTranslating.value = false;
  }
}
async function onNewDescBlur(lang: 'fr' | 'en' | 'nl') {
  if (newDescLang.value !== lang) return;
  const text = (
    lang === 'fr'
      ? newDescFr.value
      : lang === 'en'
        ? newDescEn.value
        : newDescNl.value
  ).trim();
  if (!text || newDescTranslating.value) return;
  newDescTranslating.value = true;
  try {
    const targets = (['fr', 'en', 'nl'] as const).filter((l) => l !== lang);
    const results = await Promise.all(
      targets.map((t) => libreTranslate(text, lang, t)),
    );
    targets.forEach((l, i) => {
      if (l === 'fr') newDescFr.value = results[i];
      if (l === 'en') newDescEn.value = results[i];
      if (l === 'nl') newDescNl.value = results[i];
    });
  } finally {
    newDescTranslating.value = false;
  }
}
async function confirmNew() {
  if (!newFr.value.trim() || !newOrgId.value || newSaving.value) return;
  newSaving.value = true;
  newError.value = null;
  try {
    await axiosInstance.post('products/categories/create', {
      designation: {
        fr: newFr.value.trim(),
        en: newEn.value.trim(),
        nl: newNl.value.trim(),
      },
      description: {
        fr: newDescFr.value.trim() || newFr.value.trim(),
        en: newDescEn.value.trim() || newEn.value.trim(),
        nl: newDescNl.value.trim() || newNl.value.trim(),
      },
      organizationId: newOrgId.value,
      ...(newParentId.value ? { parentId: newParentId.value } : {}),
      modules: newModules.value,
    });
    await refetchCollections();
    showNewForm.value = false;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    newError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    newSaving.value = false;
  }
}

/* ── Édition inline (nom + description) ──────────────────────────────────── */
const editingId = ref<string | null>(null);
const editLang = ref<'fr' | 'en' | 'nl'>('fr');
const editFr = ref('');
const editEn = ref('');
const editNl = ref('');
const editTranslating = ref(false);
const editDescLang = ref<'fr' | 'en' | 'nl'>('fr');
const editDescFr = ref('');
const editDescEn = ref('');
const editDescNl = ref('');
const editDescTranslating = ref(false);
const editSaving = ref(false);
const editError = ref<string | null>(null);
const editModules = ref<TCategoryModule[]>([]);
const deletingId = ref<string | null>(null);
const confirmDeleteId = ref<string | null>(null);
const confirmDeleteName = ref<string | null>(null);

async function fillMissingTranslations(
  values: Record<'fr' | 'en' | 'nl', string>,
  setters: Record<'fr' | 'en' | 'nl', (v: string) => void>,
  busy: { value: boolean },
  stillCurrent: () => boolean,
) {
  const source = (['fr', 'en', 'nl'] as const).find((l) => values[l].trim());
  if (!source) return;
  const missing = (['fr', 'en', 'nl'] as const).filter(
    (l) => l !== source && !values[l].trim(),
  );
  if (!missing.length) return;
  busy.value = true;
  try {
    const results = await Promise.all(
      missing.map((l) => libreTranslate(values[source], source, l)),
    );
    if (!stillCurrent()) return;
    missing.forEach((l, i) => setters[l](results[i]));
  } finally {
    if (stillCurrent()) busy.value = false;
  }
}

async function startEdit(cat: any) {
  editingId.value = cat.id;
  editLang.value = 'fr';
  const alt = cat.designationAlternatives;
  editFr.value = alt?.fr ?? cat.designation ?? '';
  editEn.value = alt?.en ?? '';
  editNl.value = alt?.nl ?? '';
  editDescLang.value = 'fr';
  const descAlt = cat.descriptionAlternatives;
  editDescFr.value = descAlt?.fr ?? cat.description ?? '';
  editDescEn.value = descAlt?.en ?? '';
  editDescNl.value = descAlt?.nl ?? '';
  editModules.value = Array.isArray(cat.modules) ? [...cat.modules] : [];
  editError.value = null;

  const thisEdit = cat.id;
  await fillMissingTranslations(
    { fr: editFr.value, en: editEn.value, nl: editNl.value },
    {
      fr: (v) => (editFr.value = v),
      en: (v) => (editEn.value = v),
      nl: (v) => (editNl.value = v),
    },
    editTranslating,
    () => editingId.value === thisEdit,
  );
  await fillMissingTranslations(
    { fr: editDescFr.value, en: editDescEn.value, nl: editDescNl.value },
    {
      fr: (v) => (editDescFr.value = v),
      en: (v) => (editDescEn.value = v),
      nl: (v) => (editDescNl.value = v),
    },
    editDescTranslating,
    () => editingId.value === thisEdit,
  );
}
function cancelEdit() {
  editingId.value = null;
}
async function onEditBlur(lang: 'fr' | 'en' | 'nl') {
  if (editLang.value !== lang) return;
  const text = (
    lang === 'fr' ? editFr.value : lang === 'en' ? editEn.value : editNl.value
  ).trim();
  if (!text || editTranslating.value) return;
  editTranslating.value = true;
  try {
    const targets = (['fr', 'en', 'nl'] as const).filter((l) => l !== lang);
    const results = await Promise.all(
      targets.map((t) => libreTranslate(text, lang, t)),
    );
    targets.forEach((l, i) => {
      if (l === 'fr') editFr.value = results[i];
      if (l === 'en') editEn.value = results[i];
      if (l === 'nl') editNl.value = results[i];
    });
  } finally {
    editTranslating.value = false;
  }
}
async function onEditDescBlur(lang: 'fr' | 'en' | 'nl') {
  if (editDescLang.value !== lang) return;
  const text = (
    lang === 'fr'
      ? editDescFr.value
      : lang === 'en'
        ? editDescEn.value
        : editDescNl.value
  ).trim();
  if (!text || editDescTranslating.value) return;
  editDescTranslating.value = true;
  try {
    const targets = (['fr', 'en', 'nl'] as const).filter((l) => l !== lang);
    const results = await Promise.all(
      targets.map((t) => libreTranslate(text, lang, t)),
    );
    targets.forEach((l, i) => {
      if (l === 'fr') editDescFr.value = results[i];
      if (l === 'en') editDescEn.value = results[i];
      if (l === 'nl') editDescNl.value = results[i];
    });
  } finally {
    editDescTranslating.value = false;
  }
}
async function confirmEdit() {
  if (!editingId.value || !editFr.value.trim() || editSaving.value) return;
  editSaving.value = true;
  editError.value = null;
  try {
    await axiosInstance.put(`products/categories/update/${editingId.value}`, {
      designation: {
        fr: editFr.value.trim(),
        en: editEn.value.trim(),
        nl: editNl.value.trim(),
      },
      description: {
        fr: editDescFr.value.trim() || editFr.value.trim(),
        en: editDescEn.value.trim() || editEn.value.trim(),
        nl: editDescNl.value.trim() || editNl.value.trim(),
      },
      modules: editModules.value,
    });
    await refetchCollections();
    editingId.value = null;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    editError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    editSaving.value = false;
  }
}

/* ── Suppression avec confirmation ───────────────────────────────────────── */
function askDeleteCollection(cat: any) {
  confirmDeleteId.value = cat.id;
  confirmDeleteName.value = designationOf(cat);
}
function cancelDeleteCollection() {
  confirmDeleteId.value = null;
  confirmDeleteName.value = null;
}
async function confirmDeleteCollection() {
  const catId = confirmDeleteId.value;
  if (!catId || deletingId.value) return;
  deletingId.value = catId;
  try {
    await axiosInstance.delete(`products/categories/delete/${catId}`);
    if (editingId.value === catId) editingId.value = null;
    await refetchCollections();
  } catch {
    /* 409 si la collection ne peut pas être supprimée */
  } finally {
    deletingId.value = null;
    confirmDeleteId.value = null;
    confirmDeleteName.value = null;
  }
}
</script>

<template>
  <div class="acp">
    <div class="acp__hd">
      <div>
        <div class="acp__bc">
          <span>Catalogue</span>
          <i class="pi pi-chevron-right" style="font-size: 0.625rem" />
          <span style="color: var(--nxt-primary)">{{
            t('allCollections.title')
          }}</span>
        </div>
        <h1 class="acp__title">{{ t('allCollections.title') }}</h1>
        <p class="acp__subtitle">{{ t('allCollections.subtitle') }}</p>
      </div>
      <div class="acp__hd-actions">
        <div class="acp__view-toggle">
          <button
            class="acp__view-btn"
            :class="{ 'acp__view-btn--active': viewMode === 'card' }"
            :title="t('allCollections.viewCards')"
            @click="viewMode = 'card'"
          >
            <i class="pi pi-th-large" />
          </button>
          <button
            class="acp__view-btn"
            :class="{ 'acp__view-btn--active': viewMode === 'table' }"
            :title="t('allCollections.viewTable')"
            @click="viewMode = 'table'"
          >
            <i class="pi pi-list" />
          </button>
        </div>
        <button class="acp__new-btn" @click="openNewForm()">
          <i class="pi pi-plus" /> {{ t('allCollections.newCollection') }}
        </button>
      </div>
    </div>

    <!-- Formulaire nouvelle collection -->
    <div v-if="showNewForm" class="acp__ncol">
      <div class="acp__ncol-hd">
        <div class="acp__ncol-hd-ico"><i class="pi pi-folder-plus" /></div>
        <span class="acp__ncol-title">{{
          t('allCollections.newCollection')
        }}</span>
        <div class="acp__ncol-lang-toggle">
          <button
            v-for="lang in ['fr', 'en', 'nl'] as const"
            :key="lang"
            type="button"
            class="acp__ncol-lang-btn"
            :class="{ 'acp__ncol-lang-btn--active': newLang === lang }"
            @click="newLang = lang"
          >
            {{ lang.toUpperCase() }}
          </button>
        </div>
      </div>

      <div class="acp__ncol-row">
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">{{
            t('allCollections.newOrgLabel')
          }}</label>
          <select v-model="newOrgId" class="acp__ncol-select">
            <option value="" disabled>
              {{ t('allCollections.newOrgPlaceholder') }}
            </option>
            <option v-for="o in organizations" :key="o.id" :value="o.id">
              {{ o.name ?? o.designation }}
            </option>
          </select>
        </div>
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">{{
            t('allCollections.newParentLabel')
          }}</label>
          <select
            v-model="newParentId"
            class="acp__ncol-select"
            :disabled="!newOrgId"
          >
            <option value="">
              {{ t('allCollections.newParentNone') }}
            </option>
            <option v-for="c in newOrgCategories" :key="c.id" :value="c.id">
              {{ c.designationAlternatives?.fr ?? c.designation }}
            </option>
          </select>
        </div>
      </div>

      <div class="acp__ncol-row">
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">
            <span
              class="acp__ncol-badge"
              :class="{ 'acp__ncol-badge--active': newLang === 'fr' }"
              >FR</span
            >
            Français
            <span class="acp__ncol-req">*</span>
          </label>
          <input
            v-model="newFr"
            class="acp__ncol-input"
            :class="{ 'acp__ncol-input--active': newLang === 'fr' }"
            type="text"
            placeholder="Nom en français…"
            @blur="onNewBlur('fr')"
          />
        </div>
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">
            <span
              class="acp__ncol-badge"
              :class="{ 'acp__ncol-badge--active': newLang === 'en' }"
              >EN</span
            >
            English
            <i
              v-if="newTranslating && newLang !== 'en'"
              class="pi pi-spin pi-spinner acp__ncol-spin"
            />
          </label>
          <input
            v-model="newEn"
            class="acp__ncol-input"
            :class="{ 'acp__ncol-input--active': newLang === 'en' }"
            type="text"
            placeholder="Name in English…"
            @blur="onNewBlur('en')"
          />
        </div>
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">
            <span
              class="acp__ncol-badge"
              :class="{ 'acp__ncol-badge--active': newLang === 'nl' }"
              >NL</span
            >
            Nederlands
            <i
              v-if="newTranslating && newLang !== 'nl'"
              class="pi pi-spin pi-spinner acp__ncol-spin"
            />
          </label>
          <input
            v-model="newNl"
            class="acp__ncol-input"
            :class="{ 'acp__ncol-input--active': newLang === 'nl' }"
            type="text"
            placeholder="Naam…"
            @blur="onNewBlur('nl')"
          />
        </div>
      </div>

      <div class="acp__ncol-hd" style="margin-top: 0.25rem">
        <span class="acp__ncol-title" style="font-size: 0.8125rem">{{
          t('allCollections.newDescLabel')
        }}</span>
        <div class="acp__ncol-lang-toggle">
          <button
            v-for="lang in ['fr', 'en', 'nl'] as const"
            :key="lang"
            type="button"
            class="acp__ncol-lang-btn"
            :class="{ 'acp__ncol-lang-btn--active': newDescLang === lang }"
            @click="newDescLang = lang"
          >
            {{ lang.toUpperCase() }}
          </button>
        </div>
      </div>
      <div class="acp__ncol-row">
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">
            <span
              class="acp__ncol-badge"
              :class="{ 'acp__ncol-badge--active': newDescLang === 'fr' }"
              >FR</span
            >
            Français
          </label>
          <textarea
            v-model="newDescFr"
            class="acp__ncol-input acp__ncol-textarea"
            :class="{ 'acp__ncol-input--active': newDescLang === 'fr' }"
            rows="2"
            placeholder="Description en français…"
            @blur="onNewDescBlur('fr')"
          />
        </div>
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">
            <span
              class="acp__ncol-badge"
              :class="{ 'acp__ncol-badge--active': newDescLang === 'en' }"
              >EN</span
            >
            English
            <i
              v-if="newDescTranslating && newDescLang !== 'en'"
              class="pi pi-spin pi-spinner acp__ncol-spin"
            />
          </label>
          <textarea
            v-model="newDescEn"
            class="acp__ncol-input acp__ncol-textarea"
            :class="{ 'acp__ncol-input--active': newDescLang === 'en' }"
            rows="2"
            placeholder="Description in English…"
            @blur="onNewDescBlur('en')"
          />
        </div>
        <div class="acp__ncol-field">
          <label class="acp__ncol-label">
            <span
              class="acp__ncol-badge"
              :class="{ 'acp__ncol-badge--active': newDescLang === 'nl' }"
              >NL</span
            >
            Nederlands
            <i
              v-if="newDescTranslating && newDescLang !== 'nl'"
              class="pi pi-spin pi-spinner acp__ncol-spin"
            />
          </label>
          <textarea
            v-model="newDescNl"
            class="acp__ncol-input acp__ncol-textarea"
            :class="{ 'acp__ncol-input--active': newDescLang === 'nl' }"
            rows="2"
            placeholder="Beschrijving…"
            @blur="onNewDescBlur('nl')"
          />
        </div>
      </div>

      <div class="acp__ncol-field">
        <label class="acp__ncol-label">{{
          t('allCollections.modulesLabel')
        }}</label>
        <p class="acp__modules-hint">
          {{ t('allCollections.modulesHint') }}
        </p>
        <div class="acp__modules-grid">
          <button
            v-for="m in MODULE_OPTIONS"
            :key="m.id"
            type="button"
            class="acp__module-chip"
            :class="{ 'acp__module-chip--on': newModules.includes(m.id) }"
            @click="toggleModule(newModules, m.id)"
          >
            <i :class="['pi', m.icon]" />
            {{ t(m.labelKey) }}
          </button>
        </div>
      </div>

      <div v-if="newError" class="acp__ncol-error">
        <i class="pi pi-exclamation-circle" /> {{ newError }}
      </div>
      <div class="acp__ncol-actions">
        <button
          class="acp__ncol-btn acp__ncol-btn--ghost"
          :disabled="newSaving"
          @click="closeNewForm()"
        >
          {{ t('product.cancel') }}
        </button>
        <button
          class="acp__ncol-btn acp__ncol-btn--primary"
          :disabled="newSaving || !newFr.trim() || !newOrgId"
          @click="confirmNew()"
        >
          <i v-if="newSaving" class="pi pi-spin pi-spinner" />
          <i v-else class="pi pi-check" />
          {{ newSaving ? t('product.saving') : t('product.create') }}
        </button>
      </div>
    </div>

    <div class="acp__card">
      <!-- Toolbar -->
      <div
        class="acp__toolbar"
        @click="
          orgDropdownOpen = false;
          storeDropdownOpen = false;
          tagDropdownOpen = false;
        "
      >
        <div class="acp__search-wrap">
          <i class="pi pi-search acp__search-ico" />
          <input
            :value="search"
            @input="
              (e) => {
                search = (e.target as HTMLInputElement).value;
                onSearch(search);
              }
            "
            type="text"
            :placeholder="t('allCollections.searchPlaceholder')"
            class="acp__search"
          />
          <button
            v-if="search"
            class="acp__search-clear"
            @click="
              search = '';
              searchDebounced = '';
              page = 1;
            "
          >
            <i class="pi pi-times" />
          </button>
        </div>

        <!-- Filtre organisation -->
        <div class="acp__dropdown-wrap" @click.stop>
          <button
            class="acp__dropdown-btn"
            :class="{ 'acp__dropdown-btn--active': orgFilter.length }"
            @click="orgDropdownOpen = !orgDropdownOpen"
          >
            <i class="pi pi-sitemap" />
            <span>{{ orgFilterLabel }}</span>
            <i
              v-if="orgFilter.length"
              class="pi pi-times"
              @click.stop="clearOrgFilter"
            />
            <i v-else class="pi pi-chevron-down" />
          </button>
          <Transition name="acp-drop">
            <div v-if="orgDropdownOpen" class="acp__dropdown-panel">
              <div class="acp__dropdown-search-wrap">
                <i class="pi pi-search" />
                <input
                  v-model="orgSearch"
                  class="acp__dropdown-search"
                  :placeholder="t('allCollections.searchOrg')"
                  @click.stop
                />
              </div>
              <div class="acp__dropdown-list">
                <button
                  v-for="o in filteredOrgs"
                  :key="o.id"
                  class="acp__dropdown-item"
                  :class="{
                    'acp__dropdown-item--active': orgFilter.includes(o.id),
                  }"
                  @click="toggleOrg(o.id)"
                >
                  <span
                    class="acp__dropdown-check"
                    :class="{
                      'acp__dropdown-check--on': orgFilter.includes(o.id),
                    }"
                  >
                    <i v-if="orgFilter.includes(o.id)" class="pi pi-check" />
                  </span>
                  {{ o.name ?? o.designation }}
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Filtre commerce (visible seulement si une seule organisation est sélectionnée) -->
        <div v-if="singleOrgId" class="acp__dropdown-wrap" @click.stop>
          <button
            class="acp__dropdown-btn"
            :class="{ 'acp__dropdown-btn--active': storeFilter.length }"
            @click="storeDropdownOpen = !storeDropdownOpen"
          >
            <i class="pi pi-shop" />
            <span>{{ storeFilterLabel }}</span>
            <i
              v-if="storeFilter.length"
              class="pi pi-times"
              @click.stop="clearStoreFilter"
            />
            <i v-else class="pi pi-chevron-down" />
          </button>
          <Transition name="acp-drop">
            <div v-if="storeDropdownOpen" class="acp__dropdown-panel">
              <div class="acp__dropdown-search-wrap">
                <i class="pi pi-search" />
                <input
                  v-model="storeSearch"
                  class="acp__dropdown-search"
                  :placeholder="t('allCollections.searchStore')"
                  @click.stop
                />
              </div>
              <div class="acp__dropdown-list">
                <button
                  v-for="s in filteredStores"
                  :key="s.id"
                  class="acp__dropdown-item"
                  :class="{
                    'acp__dropdown-item--active': storeFilter.includes(s.id),
                  }"
                  @click="toggleStore(s.id)"
                >
                  <span
                    class="acp__dropdown-check"
                    :class="{
                      'acp__dropdown-check--on': storeFilter.includes(s.id),
                    }"
                  >
                    <i v-if="storeFilter.includes(s.id)" class="pi pi-check" />
                  </span>
                  {{ s.designation }}
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Filtre tags -->
        <div class="acp__dropdown-wrap" @click.stop>
          <button
            class="acp__dropdown-btn"
            :class="{ 'acp__dropdown-btn--active': tagFilter.length }"
            @click="tagDropdownOpen = !tagDropdownOpen"
          >
            <i class="pi pi-hashtag" />
            <span>{{ tagFilterLabel }}</span>
            <i
              v-if="tagFilter.length"
              class="pi pi-times"
              @click.stop="clearTagFilter"
            />
            <i v-else class="pi pi-chevron-down" />
          </button>
          <Transition name="acp-drop">
            <div v-if="tagDropdownOpen" class="acp__dropdown-panel">
              <div class="acp__dropdown-search-wrap">
                <i class="pi pi-search" />
                <input
                  v-model="tagSearch"
                  class="acp__dropdown-search"
                  :placeholder="t('allCollections.searchTag')"
                  @click.stop
                />
              </div>
              <div class="acp__dropdown-list">
                <p v-if="!filteredTags.length" class="acp__dropdown-empty-hint">
                  {{ t('allCollections.noTagsHint') }}
                </p>
                <button
                  v-for="tg in filteredTags"
                  :key="tg"
                  class="acp__dropdown-item"
                  :class="{
                    'acp__dropdown-item--active': tagFilter.includes(tg),
                  }"
                  @click="toggleTag(tg)"
                >
                  <span
                    class="acp__dropdown-check"
                    :class="{
                      'acp__dropdown-check--on': tagFilter.includes(tg),
                    }"
                  >
                    <i v-if="tagFilter.includes(tg)" class="pi pi-check" />
                  </span>
                  {{ tg }}
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <span class="acp__count"
          >{{ total }}
          {{
            total !== 1
              ? t('allCollections.collectionsPlural')
              : t('allCollections.collectionSingular')
          }}</span
        >
      </div>

      <!-- En-têtes triables (vue tableau uniquement) -->
      <div v-if="viewMode === 'table'" class="acp__thead">
        <button class="acp__sort-btn" @click="toggleSort('name')">
          {{ t('allCollections.colName') }}
          <i
            class="pi acp__sort-ico"
            :class="
              sortBy === 'name'
                ? sortOrder === 'ASC'
                  ? 'pi-sort-amount-up'
                  : 'pi-sort-amount-down'
                : 'pi-sort-alt'
            "
          />
        </button>
        <span class="acp__th">{{ t('allCollections.colOrg') }}</span>
        <button class="acp__sort-btn" @click="toggleSort('position')">
          {{ t('allCollections.colPosition') }}
          <i
            class="pi acp__sort-ico"
            :class="
              sortBy === 'position'
                ? sortOrder === 'ASC'
                  ? 'pi-sort-amount-up'
                  : 'pi-sort-amount-down'
                : 'pi-sort-alt'
            "
          />
        </button>
        <button class="acp__sort-btn" @click="toggleSort('createdAt')">
          {{ t('allCollections.colCreated') }}
          <i
            class="pi acp__sort-ico"
            :class="
              sortBy === 'createdAt'
                ? sortOrder === 'ASC'
                  ? 'pi-sort-amount-up'
                  : 'pi-sort-amount-down'
                : 'pi-sort-alt'
            "
          />
        </button>
        <span class="acp__th acp__th--r">{{
          t('allCollections.colActions')
        }}</span>
      </div>

      <!-- Skeleton -->
      <div v-if="isPending" :class="viewMode === 'card' ? 'acp__cards' : ''">
        <div
          v-for="i in 6"
          :key="i"
          class="acp__row acp__row--sk"
          :class="{ 'acp__card-item acp__card-item--sk': viewMode === 'card' }"
        >
          <div class="acp__sk acp__sk--avatar" />
          <div class="acp__sk" style="width: 60%; height: 0.875rem" />
          <div class="acp__sk" style="width: 40%; height: 0.75rem" />
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="!rows.length" class="acp__empty">
        <i class="pi pi-folder-open" />
        <span>{{ t('allCollections.empty') }}</span>
      </div>

      <!-- Édition inline (au-dessus des listes, table ou carte) -->
      <div v-if="editingId" class="acp__ncol acp__ncol--row">
        <div class="acp__ncol-hd">
          <div class="acp__ncol-hd-ico"><i class="pi pi-pencil" /></div>
          <span class="acp__ncol-title">{{
            t('allCollections.editCollection')
          }}</span>
          <div class="acp__ncol-lang-toggle">
            <button
              v-for="lang in ['fr', 'en', 'nl'] as const"
              :key="lang"
              type="button"
              class="acp__ncol-lang-btn"
              :class="{ 'acp__ncol-lang-btn--active': editLang === lang }"
              @click="editLang = lang"
            >
              {{ lang.toUpperCase() }}
            </button>
          </div>
        </div>
        <div class="acp__ncol-row">
          <div class="acp__ncol-field">
            <label class="acp__ncol-label">
              <span
                class="acp__ncol-badge"
                :class="{ 'acp__ncol-badge--active': editLang === 'fr' }"
                >FR</span
              >
              Français
              <span class="acp__ncol-req">*</span>
            </label>
            <input
              v-model="editFr"
              class="acp__ncol-input"
              :class="{ 'acp__ncol-input--active': editLang === 'fr' }"
              type="text"
              @blur="onEditBlur('fr')"
              @keyup.enter="confirmEdit()"
              @keyup.escape="cancelEdit()"
            />
          </div>
          <div class="acp__ncol-field">
            <label class="acp__ncol-label">
              <span
                class="acp__ncol-badge"
                :class="{ 'acp__ncol-badge--active': editLang === 'en' }"
                >EN</span
              >
              English
              <i
                v-if="editTranslating && editLang !== 'en'"
                class="pi pi-spin pi-spinner acp__ncol-spin"
              />
            </label>
            <input
              v-model="editEn"
              class="acp__ncol-input"
              :class="{ 'acp__ncol-input--active': editLang === 'en' }"
              type="text"
              @blur="onEditBlur('en')"
              @keyup.enter="confirmEdit()"
              @keyup.escape="cancelEdit()"
            />
          </div>
          <div class="acp__ncol-field">
            <label class="acp__ncol-label">
              <span
                class="acp__ncol-badge"
                :class="{ 'acp__ncol-badge--active': editLang === 'nl' }"
                >NL</span
              >
              Nederlands
              <i
                v-if="editTranslating && editLang !== 'nl'"
                class="pi pi-spin pi-spinner acp__ncol-spin"
              />
            </label>
            <input
              v-model="editNl"
              class="acp__ncol-input"
              :class="{ 'acp__ncol-input--active': editLang === 'nl' }"
              type="text"
              @blur="onEditBlur('nl')"
              @keyup.enter="confirmEdit()"
              @keyup.escape="cancelEdit()"
            />
          </div>
        </div>

        <div class="acp__ncol-hd" style="margin-top: 0.25rem">
          <span class="acp__ncol-title" style="font-size: 0.8125rem">{{
            t('allCollections.newDescLabel')
          }}</span>
          <div class="acp__ncol-lang-toggle">
            <button
              v-for="lang in ['fr', 'en', 'nl'] as const"
              :key="lang"
              type="button"
              class="acp__ncol-lang-btn"
              :class="{ 'acp__ncol-lang-btn--active': editDescLang === lang }"
              @click="editDescLang = lang"
            >
              {{ lang.toUpperCase() }}
            </button>
          </div>
        </div>
        <div class="acp__ncol-row">
          <div class="acp__ncol-field">
            <label class="acp__ncol-label">
              <span
                class="acp__ncol-badge"
                :class="{ 'acp__ncol-badge--active': editDescLang === 'fr' }"
                >FR</span
              >
              Français
            </label>
            <textarea
              v-model="editDescFr"
              class="acp__ncol-input acp__ncol-textarea"
              :class="{ 'acp__ncol-input--active': editDescLang === 'fr' }"
              rows="2"
              @blur="onEditDescBlur('fr')"
            />
          </div>
          <div class="acp__ncol-field">
            <label class="acp__ncol-label">
              <span
                class="acp__ncol-badge"
                :class="{ 'acp__ncol-badge--active': editDescLang === 'en' }"
                >EN</span
              >
              English
              <i
                v-if="editDescTranslating && editDescLang !== 'en'"
                class="pi pi-spin pi-spinner acp__ncol-spin"
              />
            </label>
            <textarea
              v-model="editDescEn"
              class="acp__ncol-input acp__ncol-textarea"
              :class="{ 'acp__ncol-input--active': editDescLang === 'en' }"
              rows="2"
              @blur="onEditDescBlur('en')"
            />
          </div>
          <div class="acp__ncol-field">
            <label class="acp__ncol-label">
              <span
                class="acp__ncol-badge"
                :class="{ 'acp__ncol-badge--active': editDescLang === 'nl' }"
                >NL</span
              >
              Nederlands
              <i
                v-if="editDescTranslating && editDescLang !== 'nl'"
                class="pi pi-spin pi-spinner acp__ncol-spin"
              />
            </label>
            <textarea
              v-model="editDescNl"
              class="acp__ncol-input acp__ncol-textarea"
              :class="{ 'acp__ncol-input--active': editDescLang === 'nl' }"
              rows="2"
              @blur="onEditDescBlur('nl')"
            />
          </div>
        </div>

        <div class="acp__ncol-field">
          <label class="acp__ncol-label">{{
            t('allCollections.modulesLabel')
          }}</label>
          <p class="acp__modules-hint">
            {{ t('allCollections.modulesHint') }}
          </p>
          <div class="acp__modules-grid">
            <button
              v-for="m in MODULE_OPTIONS"
              :key="m.id"
              type="button"
              class="acp__module-chip"
              :class="{ 'acp__module-chip--on': editModules.includes(m.id) }"
              @click="toggleModule(editModules, m.id)"
            >
              <i :class="['pi', m.icon]" />
              {{ t(m.labelKey) }}
            </button>
          </div>
        </div>

        <div v-if="editError" class="acp__ncol-error">
          <i class="pi pi-exclamation-circle" /> {{ editError }}
        </div>
        <div class="acp__ncol-actions">
          <button
            class="acp__ncol-btn acp__ncol-btn--ghost"
            :disabled="editSaving"
            @click="cancelEdit()"
          >
            {{ t('product.cancel') }}
          </button>
          <button
            class="acp__ncol-btn acp__ncol-btn--primary"
            :disabled="editSaving || !editFr.trim()"
            @click="confirmEdit()"
          >
            <i v-if="editSaving" class="pi pi-spin pi-spinner" />
            <i v-else class="pi pi-check" />
            {{ editSaving ? t('product.saving') : t('product.save') }}
          </button>
        </div>
      </div>

      <!-- Vue carte -->
      <div
        v-else-if="!isPending && rows.length && viewMode === 'card'"
        class="acp__cards"
      >
        <div v-for="cat in rows" :key="cat.id" class="acp__card-item">
          <div class="acp__avatar" :style="avatarStyle(cat.id)">
            {{ initials(designationOf(cat)) }}
          </div>
          <span class="acp__card-item-name">{{ designationOf(cat) }}</span>
          <button
            class="acp__card-item-org"
            @click="cat.organizationId && goToOrg(cat.organizationId)"
          >
            {{ cat.organization?.name ?? '—' }}
          </button>
          <div class="acp__card-item-actions">
            <button
              class="acp__icon-btn"
              :title="t('product.edit')"
              @click="startEdit(cat)"
            >
              <i class="pi pi-pencil" />
            </button>
            <button
              class="acp__icon-btn acp__icon-btn--danger"
              :disabled="deletingId === cat.id"
              :title="t('product.delete')"
              @click="askDeleteCollection(cat)"
            >
              <i v-if="deletingId === cat.id" class="pi pi-spin pi-spinner" />
              <i v-else class="pi pi-trash" />
            </button>
            <button
              class="acp__btn-view acp__btn-view--sm"
              :disabled="!cat.organizationId"
              @click="cat.organizationId && goToStores(cat.organizationId)"
            >
              <i class="pi pi-shop" />
            </button>
          </div>
        </div>
      </div>

      <!-- Vue tableau -->
      <div v-else-if="!isPending && rows.length">
        <div v-for="cat in rows" :key="cat.id" class="acp__row">
          <div class="acp__cell acp__cell--name">
            <div
              class="acp__avatar acp__avatar--sm"
              :style="avatarStyle(cat.id)"
            >
              {{ initials(designationOf(cat)) }}
            </div>
            <span class="acp__cell-name">{{ designationOf(cat) }}</span>
          </div>
          <button
            class="acp__cell acp__cell--org"
            @click="cat.organizationId && goToOrg(cat.organizationId)"
          >
            {{ cat.organization?.name ?? '—' }}
          </button>
          <span class="acp__cell">{{ cat.position ?? '—' }}</span>
          <span class="acp__cell">{{
            cat.createdAt ? new Date(cat.createdAt).toLocaleDateString() : '—'
          }}</span>
          <div class="acp__cell acp__cell--actions">
            <button
              class="acp__icon-btn"
              :title="t('product.edit')"
              @click="startEdit(cat)"
            >
              <i class="pi pi-pencil" />
            </button>
            <button
              class="acp__icon-btn acp__icon-btn--danger"
              :disabled="deletingId === cat.id"
              :title="t('product.delete')"
              @click="askDeleteCollection(cat)"
            >
              <i v-if="deletingId === cat.id" class="pi pi-spin pi-spinner" />
              <i v-else class="pi pi-trash" />
            </button>
            <button
              class="acp__btn-view"
              :disabled="!cat.organizationId"
              @click="cat.organizationId && goToStores(cat.organizationId)"
            >
              <i class="pi pi-shop" />
              {{ t('allCollections.viewStore') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!isPending && total > 0" class="acp__pagination">
        <div class="acp__pg-left">
          <span class="acp__pg-info">
            {{ (page - 1) * pageSize + 1 }}–{{
              Math.min(page * pageSize, total)
            }}
            {{ t('allCollections.paginationOf') }} {{ total }}
          </span>
          <select v-model.number="pageSize" class="acp__pg-size">
            <option v-for="sz in PAGE_SIZES" :key="sz" :value="sz">
              {{ sz }} {{ t('allCollections.perPage') }}
            </option>
          </select>
        </div>
        <div class="acp__pg-btns">
          <button
            class="acp__pg-btn"
            :disabled="page === 1"
            @click="setPage(1)"
          >
            <i class="pi pi-angle-double-left" />
          </button>
          <button
            class="acp__pg-btn"
            :disabled="page === 1"
            @click="setPage(page - 1)"
          >
            <i class="pi pi-angle-left" />
          </button>
          <button
            v-for="n in visiblePages"
            :key="n"
            class="acp__pg-btn"
            :class="{ 'acp__pg-btn--active': n === page }"
            @click="setPage(n)"
          >
            {{ n }}
          </button>
          <button
            class="acp__pg-btn"
            :disabled="page === totalPages"
            @click="setPage(page + 1)"
          >
            <i class="pi pi-angle-right" />
          </button>
          <button
            class="acp__pg-btn"
            :disabled="page === totalPages"
            @click="setPage(totalPages)"
          >
            <i class="pi pi-angle-double-right" />
          </button>
        </div>
      </div>
    </div>

    <CatalogueDeleteConfirm
      :model-value="confirmDeleteName"
      @confirm="confirmDeleteCollection"
      @cancel="cancelDeleteCollection"
    />
  </div>
</template>

<style scoped>
.acp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.75rem 2rem 3rem;
  width: 100%;
}
.acp__hd {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.acp__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #aaa;
  margin-bottom: 0.25rem;
}
.acp__title {
  margin: 0;
  font-size: 1.625rem;
  font-weight: 800;
  color: #111;
  letter-spacing: -0.02em;
}
[data-theme='dark'] .acp__title {
  color: #f0f0f0;
}
.acp__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-variant, #767676);
}

.acp__card {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}
[data-theme='dark'] .acp__card {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}

/* Toolbar */
.acp__toolbar {
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
  flex-wrap: wrap;
}
.acp__search-wrap {
  position: relative;
  flex: 1;
  min-width: 12rem;
  max-width: 340px;
}
.acp__search-ico {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  font-size: 0.8rem;
  color: var(--nxt-on-surface-variant);
  pointer-events: none;
}
.acp__search {
  width: 100%;
  padding: 0.55rem 0.75rem 0.55rem 2.1rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  background: var(--nxt-surface-low, #f8f8fa);
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.acp__search:focus {
  border-color: var(--nxt-primary);
  background: var(--nxt-card-bg, #fff);
}
.acp__search-clear {
  position: absolute;
  top: 50%;
  right: 0.5rem;
  transform: translateY(-50%);
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  border: none;
  background: var(--nxt-outline-variant, #ddd);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  color: var(--nxt-on-surface-variant);
}
.acp__count {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  margin-left: auto;
}

/* Dropdown organisation */
.acp__dropdown-wrap {
  position: relative;
}
.acp__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.acp__dropdown-btn--active {
  border-color: #0b1320;
  color: #0b1320;
}
[data-theme='dark'] .acp__dropdown-btn--active {
  border-color: #fff;
  color: #fff;
}
.acp__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 200;
  min-width: 18rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.acp__dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.acp__dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.acp__dropdown-list {
  max-height: 260px;
  overflow-y: auto;
  padding: 0.375rem;
}
.acp__dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.acp__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.acp__dropdown-item--active {
  font-weight: 700;
}
.acp__dropdown-check {
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 0.375rem;
  border: 1.5px solid var(--nxt-outline-variant, #ccc);
  background: var(--nxt-card-bg, #fff);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.625rem;
  flex-shrink: 0;
  transition: all 0.15s;
}
.acp__dropdown-check--on {
  background: #0b1320;
  border-color: #0b1320;
  color: #fff;
}
[data-theme='dark'] .acp__dropdown-check--on {
  background: #fff;
  border-color: #fff;
  color: #0b1320;
}
.acp-drop-enter-active,
.acp-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.acp-drop-enter-from,
.acp-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Table head */
.acp__thead {
  display: grid;
  grid-template-columns: 1.3fr 1.3fr 0.6fr 0.8fr 190px;
  gap: 1rem;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
}
.acp__th {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.55;
}
.acp__th--r {
  text-align: right;
}
.acp__sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
  opacity: 0.55;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  transition: opacity 0.14s;
  justify-self: start;
}
.acp__sort-btn:hover {
  opacity: 0.9;
}
.acp__sort-ico {
  font-size: 0.65rem;
}

/* Rows */
.acp__row {
  display: grid;
  grid-template-columns: 1.3fr 1.3fr 0.6fr 0.8fr 190px;
  gap: 1rem;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.05));
}
.acp__row:last-child {
  border-bottom: none;
}
.acp__cell {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-variant);
}
.acp__cell--name {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.acp__cell-ico {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  flex-shrink: 0;
  background: #111;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}
.acp__cell-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.acp__cell--org {
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-primary, #f68402);
  cursor: pointer;
  text-align: left;
  padding: 0;
}
.acp__cell--org:hover {
  text-decoration: underline;
}
.acp__cell--actions {
  display: flex;
  justify-content: flex-end;
}
.acp__btn-view {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.875rem;
  border-radius: 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #ddd);
  background: #fff;
  color: var(--nxt-on-surface);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}
.acp__btn-view:hover:not(:disabled) {
  border-color: #111;
}
.acp__btn-view:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
[data-theme='dark'] .acp__btn-view {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f0f0f0;
}

/* Skeleton */
.acp__row--sk {
  align-items: center;
}
.acp__sk {
  border-radius: 0.375rem;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.06) 25%,
    rgba(0, 0, 0, 0.1) 50%,
    rgba(0, 0, 0, 0.06) 75%
  );
  background-size: 200% 100%;
  animation: acp-shimmer 1.4s infinite;
}
@keyframes acp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Empty */
.acp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-variant);
}
.acp__empty .pi {
  font-size: 1.75rem;
  opacity: 0.3;
}

/* Pagination */
.acp__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.02));
}
.acp__pg-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.acp__pg-info {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.acp__pg-size {
  font-size: 0.75rem;
  font-family: inherit;
  padding: 0.25rem 0.5rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.375rem;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  cursor: pointer;
  outline: none;
}
.acp__pg-btns {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.acp__pg-btn {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.375rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  background: var(--nxt-card-bg, #fff);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: var(--nxt-on-surface-variant);
  transition:
    background 0.12s,
    border-color 0.12s;
}
.acp__pg-btn--active {
  background: #111;
  border-color: #111;
  color: #fff;
}
.acp__pg-btn:hover:not(:disabled) {
  border-color: var(--nxt-primary);
  color: var(--nxt-primary);
}
.acp__pg-btn--active:hover {
  color: #fff;
}
.acp__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* Header actions */
.acp__hd-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.acp__view-toggle {
  display: flex;
  border: 1px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.625rem;
  overflow: hidden;
  flex-shrink: 0;
}
.acp__view-btn {
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  cursor: pointer;
}
.acp__view-btn--active {
  background: #111;
  color: #fff;
}
.acp__new-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.625rem 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
  box-shadow: 0 4px 14px -3px rgba(0, 0, 0, 0.4);
  transition: all 0.15s;
  white-space: nowrap;
}
.acp__new-btn:hover {
  box-shadow: 0 6px 20px -3px rgba(0, 0, 0, 0.5);
  transform: translateY(-1px);
}

/* Dropdown empty hint */
.acp__dropdown-empty-hint {
  padding: 0.75rem;
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted, #938282);
  text-align: center;
}

/* Formulaire création/édition collection */
.acp__ncol {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.acp__ncol--row {
  margin: 0 1.25rem 0.75rem;
}
[data-theme='dark'] .acp__ncol {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.1);
}
.acp__ncol-hd {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.acp__ncol-hd-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.06);
  color: #0b1320;
  font-size: 1rem;
  flex-shrink: 0;
}
[data-theme='dark'] .acp__ncol-hd-ico {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
.acp__ncol-title {
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--nxt-on-surface, #171310);
  letter-spacing: -0.01em;
}
[data-theme='dark'] .acp__ncol-title {
  color: #f5f2ee;
}
.acp__ncol-lang-toggle {
  display: flex;
  gap: 0.25rem;
  margin-left: auto;
  padding: 0.1875rem;
  border-radius: 0.625rem;
  background: rgba(0, 0, 0, 0.04);
}
[data-theme='dark'] .acp__ncol-lang-toggle {
  background: rgba(255, 255, 255, 0.06);
}
.acp__ncol-lang-btn {
  padding: 0.3125rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  transition: all 0.15s;
}
.acp__ncol-lang-btn--active {
  background: #fff;
  color: #0b1320;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
[data-theme='dark'] .acp__ncol-lang-btn--active {
  background: #2a2d33;
  color: #fff;
}
.acp__ncol-field {
  flex: 1;
  min-width: 12rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.acp__ncol-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.acp__ncol-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
}
.acp__ncol-req {
  color: #ef4444;
}
.acp__ncol-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.25rem;
  padding: 0 0.3rem;
  border-radius: 0.3125rem;
  font-size: 0.625rem;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.06);
  color: var(--nxt-on-surface-variant);
}
[data-theme='dark'] .acp__ncol-badge {
  background: rgba(255, 255, 255, 0.08);
}
.acp__ncol-badge--active {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .acp__ncol-badge--active {
  background: #fff;
  color: #0b1320;
}
.acp__ncol-spin {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-variant);
  margin-left: auto;
}
.acp__ncol-input,
.acp__ncol-select {
  height: 2.5rem;
  padding: 0 0.875rem;
  border-radius: 0.625rem;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface, #171310);
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}
.acp__ncol-input::placeholder {
  color: #b7b2ab;
}
.acp__ncol-input:focus,
.acp__ncol-select:focus {
  border-color: #0b1320;
  box-shadow: 0 0 0 3px rgba(11, 19, 32, 0.1);
}
[data-theme='dark'] .acp__ncol-input:focus,
[data-theme='dark'] .acp__ncol-select:focus {
  border-color: #fff;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
}
.acp__ncol-input--active {
  border-color: rgba(11, 19, 32, 0.4);
}
[data-theme='dark'] .acp__ncol-input--active {
  border-color: rgba(255, 255, 255, 0.4);
}
.acp__ncol-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
[data-theme='dark'] .acp__ncol-input,
[data-theme='dark'] .acp__ncol-select {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f5f2ee;
}
.acp__ncol-textarea {
  height: auto;
  min-height: 3.5rem;
  padding: 0.5rem 0.875rem;
  resize: vertical;
}
.acp__ncol-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.08);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 500;
}
.acp__modules-hint {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.acp__modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.5rem;
}
.acp__module-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}
.acp__module-chip--on {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.1);
  color: #0b1320;
}
[data-theme='dark'] .acp__module-chip--on {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}
.acp__ncol-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
}
.acp__ncol-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5625rem 1.125rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.acp__ncol-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.acp__ncol-btn--ghost {
  background: transparent;
  border: 1.5px solid rgba(0, 0, 0, 0.1);
  color: var(--nxt-on-surface-variant, #6b6b6b);
}
.acp__ncol-btn--ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.03);
}
[data-theme='dark'] .acp__ncol-btn--ghost {
  border-color: rgba(255, 255, 255, 0.12);
  color: #cfcac2;
}
.acp__ncol-btn--primary {
  background: linear-gradient(135deg, #333, #111);
  color: #fff;
}
.acp__ncol-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px -3px rgba(0, 0, 0, 0.4);
}

/* Avatar */
.acp__avatar {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 800;
  flex-shrink: 0;
}
.acp__avatar--sm {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  font-size: 0.6875rem;
  border-radius: 0.5rem;
}

/* Icon buttons (edit/delete) */
.acp__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.125rem;
  height: 2.125rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;
}
[data-theme='dark'] .acp__icon-btn {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.1);
}
.acp__icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.acp__icon-btn--danger:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}
.acp__btn-view--sm {
  padding: 0.4rem 0.625rem;
}

/* Vue carte */
.acp__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 1.25rem;
  padding: 1.25rem;
}
.acp__card-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.625rem;
  padding: 1.5rem 1.25rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 1.25rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  transition:
    box-shadow 0.3s cubic-bezier(0.2, 1, 0.3, 1),
    transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}
[data-theme='dark'] .acp__card-item {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}
.acp__card-item:hover {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.acp__card-item--sk {
  align-items: center;
}
.acp__card-item-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.acp__card-item-org {
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-primary, #f68402);
  cursor: pointer;
  padding: 0;
}
.acp__card-item-org:hover {
  text-decoration: underline;
}
.acp__card-item-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

/* Skeleton */
.acp__sk--avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.625rem;
  flex-shrink: 0;
}
</style>
